import { useState } from "react";
import axios from "axios";
import {
  MapPin,
  Clock,
  Phone,
  Mail,
  Send,
  CheckCircle2,
  Loader2,
  Instagram,
  Facebook,
  Linkedin,
} from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { SITE } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Konstruksi Bangunan",
  "Renovasi Rumah dan Gedung",
  "Desain dan Pembangunan",
  "Pekerjaan Interior",
  "Pemeliharaan dan Perbaikan",
  "Konsultasi Proyek",
  "Lainnya",
];

const EMPTY_FORM = {
  nama: "",
  whatsapp: "",
  email: "",
  jenis_layanan: "",
  lokasi: "",
  pesan: "",
};

const inputClass =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 transition-[border-color,box-shadow] duration-200 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/30";

export const Contact = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const setField = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (form.nama.trim().length < 2) err.nama = "Mohon isi nama Anda.";
    if (!/^[0-9+\-\s]{8,20}$/.test(form.whatsapp.trim()))
      err.whatsapp = "Mohon isi nomor WhatsApp yang valid.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      err.email = "Mohon isi alamat email yang valid.";
    if (!form.jenis_layanan) err.jenis_layanan = "Pilih jenis layanan.";
    if (form.lokasi.trim().length < 2) err.lokasi = "Mohon isi lokasi proyek.";
    if (form.pesan.trim().length < 10)
      err.pesan = "Ceritakan kebutuhan Anda (minimal 10 karakter).";
    return err;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length > 0) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        nama: form.nama.trim(),
        whatsapp: form.whatsapp.trim(),
        email: form.email.trim(),
        lokasi: form.lokasi.trim(),
        pesan: form.pesan.trim(),
      });
      setStatus("success");
      setForm(EMPTY_FORM);
    } catch {
      setStatus("error");
    }
  };

  const infoItems = [
    {
      icon: MapPin,
      label: "Alamat Kantor",
      value: SITE.address || "Segera tersedia",
      testId: "contact-info-address",
    },
    {
      icon: Clock,
      label: "Jam Operasional",
      value: SITE.workingHours || "Segera tersedia",
      testId: "contact-info-hours",
    },
    {
      icon: Phone,
      label: "WhatsApp",
      value: SITE.whatsappDisplay,
      testId: "contact-info-whatsapp",
    },
    {
      icon: Mail,
      label: "Email",
      value: SITE.email || "Segera tersedia",
      testId: "contact-info-email",
    },
  ];

  return (
    <section id="kontak" className="bg-mist" aria-label="Kontak">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 py-20 sm:px-6 lg:grid-cols-5 lg:gap-16 lg:px-12 lg:py-28">
        <Reveal className="lg:col-span-2">
          <Eyebrow>Kontak</Eyebrow>
          <h2 className="text-balance font-heading text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-4xl">
            Mari Diskusikan{" "}
            <span className="italic text-orange-500">Proyek Anda</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Isi formulir di samping atau hubungi kami melalui WhatsApp. Tim kami
            akan merespons pesan Anda secepatnya.
          </p>

          <ul className="mt-9 space-y-5">
            {infoItems.map((item) => (
              <li key={item.testId} data-testid={item.testId} className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy">
                  <item.icon className="h-5 w-5 text-orange-400" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-heading text-sm font-bold text-navy">
                    {item.label}
                  </span>
                  <span
                    className={`mt-0.5 block text-sm ${
                      item.value === "Segera tersedia"
                        ? "italic text-slate-400"
                        : "text-slate-600"
                    }`}
                  >
                    {item.value}
                  </span>
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-9">
            <p className="font-heading text-sm font-bold text-navy">Media Sosial</p>
            <div className="mt-3 flex gap-3">
              {[
                { icon: Instagram, label: "Instagram" },
                { icon: Facebook, label: "Facebook" },
                { icon: Linkedin, label: "LinkedIn" },
              ].map((s) => (
                <span
                  key={s.label}
                  data-testid={`social-${s.label.toLowerCase()}`}
                  title={`${s.label} — segera tersedia`}
                  aria-label={`${s.label} (segera tersedia)`}
                  className="flex h-11 w-11 cursor-not-allowed items-center justify-center rounded-xl border border-dashed border-slate-300 text-slate-400"
                >
                  <s.icon className="h-5 w-5" aria-hidden="true" />
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-3">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            noValidate
            className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-navy/5 sm:p-9"
          >
            {status === "success" && (
              <div
                data-testid="contact-success-message"
                role="status"
                className="mb-6 flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4 text-sm font-medium text-green-800"
              >
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" aria-hidden="true" />
                Terima kasih! Pesan Anda telah kami terima. Tim kami akan segera
                menghubungi Anda untuk konsultasi.
              </div>
            )}
            {status === "error" && (
              <div
                data-testid="contact-error-message"
                role="alert"
                className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700"
              >
                Maaf, pesan gagal terkirim. Silakan coba beberapa saat lagi.
              </div>
            )}

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="input-nama" className="mb-1.5 block text-sm font-semibold text-navy">
                  Nama <span className="text-orange-600" aria-hidden="true">*</span>
                </label>
                <input
                  id="input-nama"
                  data-testid="input-nama"
                  type="text"
                  autoComplete="name"
                  placeholder="Nama lengkap Anda"
                  value={form.nama}
                  onChange={setField("nama")}
                  aria-invalid={!!errors.nama}
                  className={inputClass}
                />
                {errors.nama && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{errors.nama}</p>}
              </div>
              <div>
                <label htmlFor="input-whatsapp" className="mb-1.5 block text-sm font-semibold text-navy">
                  Nomor WhatsApp <span className="text-orange-600" aria-hidden="true">*</span>
                </label>
                <input
                  id="input-whatsapp"
                  data-testid="input-whatsapp"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="cth. 0812 3456 7890"
                  value={form.whatsapp}
                  onChange={setField("whatsapp")}
                  aria-invalid={!!errors.whatsapp}
                  className={inputClass}
                />
                {errors.whatsapp && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{errors.whatsapp}</p>}
              </div>
              <div>
                <label htmlFor="input-email" className="mb-1.5 block text-sm font-semibold text-navy">
                  Email <span className="text-orange-600" aria-hidden="true">*</span>
                </label>
                <input
                  id="input-email"
                  data-testid="input-email"
                  type="email"
                  autoComplete="email"
                  placeholder="nama@email.com"
                  value={form.email}
                  onChange={setField("email")}
                  aria-invalid={!!errors.email}
                  className={inputClass}
                />
                {errors.email && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="select-layanan" className="mb-1.5 block text-sm font-semibold text-navy">
                  Jenis Layanan <span className="text-orange-600" aria-hidden="true">*</span>
                </label>
                <select
                  id="select-layanan"
                  data-testid="select-layanan"
                  value={form.jenis_layanan}
                  onChange={setField("jenis_layanan")}
                  aria-invalid={!!errors.jenis_layanan}
                  className={`${inputClass} ${form.jenis_layanan ? "" : "text-slate-400"}`}
                >
                  <option value="" disabled>
                    Pilih jenis layanan
                  </option>
                  {SERVICE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {errors.jenis_layanan && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{errors.jenis_layanan}</p>}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="input-lokasi" className="mb-1.5 block text-sm font-semibold text-navy">
                  Lokasi Proyek <span className="text-orange-600" aria-hidden="true">*</span>
                </label>
                <input
                  id="input-lokasi"
                  data-testid="input-lokasi"
                  type="text"
                  placeholder="Kota / area proyek Anda"
                  value={form.lokasi}
                  onChange={setField("lokasi")}
                  aria-invalid={!!errors.lokasi}
                  className={inputClass}
                />
                {errors.lokasi && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{errors.lokasi}</p>}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="textarea-pesan" className="mb-1.5 block text-sm font-semibold text-navy">
                  Pesan <span className="text-orange-600" aria-hidden="true">*</span>
                </label>
                <textarea
                  id="textarea-pesan"
                  data-testid="textarea-pesan"
                  rows={5}
                  placeholder="Ceritakan kebutuhan proyek Anda: jenis pekerjaan, perkiraan ukuran, target waktu, dan lainnya."
                  value={form.pesan}
                  onChange={setField("pesan")}
                  aria-invalid={!!errors.pesan}
                  className={`${inputClass} resize-y`}
                />
                {errors.pesan && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{errors.pesan}</p>}
              </div>
            </div>

            <button
              type="submit"
              data-testid="submit-button"
              disabled={status === "loading"}
              className="group mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-orange-500 px-8 py-4 text-sm font-bold text-white transition-[background-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Mengirim…
                </>
              ) : (
                <>
                  Kirim Pesan
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
